import { AlertTriangle, Calendar, Check, Edit3, Key, Loader2, Pause, Play, Save, Shield, Users, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useToast } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { licenseService } from "@/lib/services";
import type { LicenseResponse, LicenseStatus, LicenseStatusResponse } from "@/types";

// ---------------------------------------------------------------------------
// Status badge component
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: LicenseStatus }) {
  const config: Record<LicenseStatus, { label: string; className: string }> = {
    active: { label: "Active", className: "bg-green-500/20 text-green-400 border-green-500/30" },
    expired: { label: "Expired", className: "bg-red-500/20 text-red-400 border-red-500/30" },
    suspended: { label: "Suspended", className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
    seat_limit_reached: {
      label: "Seat Limit Reached",
      className: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    },
  };

  const { label, className } = config[status] ?? config.active;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${className}`}>
      {status === "active" && <Check className="h-3 w-3" />}
      {status === "suspended" && <Pause className="h-3 w-3" />}
      {status === "expired" && <AlertTriangle className="h-3 w-3" />}
      {status === "seat_limit_reached" && <Users className="h-3 w-3" />}
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AdminLicense() {
  const { user } = useAuth();
  const { showToast } = useToast();

  const isMhStaff = user?.role === "install_admin";

  // State
  const [loading, setLoading] = useState(true);
  const [licenseStatus, setLicenseStatus] = useState<LicenseStatusResponse | null>(null);
  const [licenseDetail, setLicenseDetail] = useState<LicenseResponse | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suspending, setSuspending] = useState(false);

  // Edit form state
  const [editSeatLimit, setEditSeatLimit] = useState<string>("");
  const [editExpiresAt, setEditExpiresAt] = useState<string>("");
  const [editLicenseName, setEditLicenseName] = useState<string>("");
  const [editNotes, setEditNotes] = useState<string>("");
  const [editExemptDomains, setEditExemptDomains] = useState<string>("");
  const [suspendReason, setSuspendReason] = useState<string>("");
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);

  // Load data
  useEffect(() => {
    loadData();
  }, [isMhStaff]);

  async function loadData() {
    setLoading(true);
    try {
      if (isMhStaff) {
        const res = await licenseService.getDetail();
        setLicenseDetail(res.data);
        // Also derive status from detail
        setLicenseStatus({
          status: res.data.status,
          seatLimit: res.data.seatLimit,
          seatsUsed: res.data.seatsUsed,
          seatsAvailable: res.data.seatLimit != null ? Math.max(0, res.data.seatLimit - res.data.seatsUsed) : null,
          expiresAt: res.data.expiresAt,
          isActive: res.data.isActive,
        });
      } else {
        const res = await licenseService.getStatus();
        setLicenseStatus(res.data);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to load license information";
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  }

  function startEditing() {
    if (!licenseDetail) return;
    setEditSeatLimit(licenseDetail.seatLimit?.toString() ?? "");
    setEditExpiresAt(licenseDetail.expiresAt ? licenseDetail.expiresAt.slice(0, 16) : "");
    setEditLicenseName(licenseDetail.licenseName ?? "");
    setEditNotes(licenseDetail.notes ?? "");
    setEditExemptDomains(licenseDetail.exemptDomains?.join(", ") ?? "mutuallyhuman.com");
    setEditing(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const res = await licenseService.update({
        seatLimit: editSeatLimit ? parseInt(editSeatLimit, 10) : null,
        expiresAt: editExpiresAt ? new Date(editExpiresAt).toISOString() : null,
        licenseName: editLicenseName || null,
        notes: editNotes || null,
        exemptDomains: editExemptDomains
          ? editExemptDomains
              .split(",")
              .map((d) => d.trim())
              .filter(Boolean)
          : ["mutuallyhuman.com"],
      });
      setLicenseDetail(res.data);
      setLicenseStatus({
        status: res.data.status,
        seatLimit: res.data.seatLimit,
        seatsUsed: res.data.seatsUsed,
        seatsAvailable: res.data.seatLimit != null ? Math.max(0, res.data.seatLimit - res.data.seatsUsed) : null,
        expiresAt: res.data.expiresAt,
        isActive: res.data.isActive,
      });
      setEditing(false);
      showToast("License updated successfully", "success");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to update license";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleSuspend() {
    setSuspending(true);
    try {
      const res = await licenseService.suspend({ reason: suspendReason || null });
      setLicenseDetail(res.data);
      setLicenseStatus({
        status: res.data.status,
        seatLimit: res.data.seatLimit,
        seatsUsed: res.data.seatsUsed,
        seatsAvailable: res.data.seatLimit != null ? Math.max(0, res.data.seatLimit - res.data.seatsUsed) : null,
        expiresAt: res.data.expiresAt,
        isActive: res.data.isActive,
      });
      setSuspendReason("");
      showToast("Platform suspended", "success");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to suspend platform";
      showToast(message, "error");
    } finally {
      setSuspending(false);
    }
  }

  async function handleResume() {
    try {
      const res = await licenseService.resume();
      setLicenseDetail(res.data);
      setLicenseStatus({
        status: res.data.status,
        seatLimit: res.data.seatLimit,
        seatsUsed: res.data.seatsUsed,
        seatsAvailable: res.data.seatLimit != null ? Math.max(0, res.data.seatLimit - res.data.seatsUsed) : null,
        expiresAt: res.data.expiresAt,
        isActive: res.data.isActive,
      });
      showToast("Platform resumed", "success");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to resume platform";
      showToast(message, "error");
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-content-tertiary" />
      </div>
    );
  }

  if (!licenseStatus) {
    return (
      <div className="text-center py-12 text-content-tertiary">
        <Key className="h-8 w-8 mx-auto mb-3 opacity-50" />
        <p>No license information available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Status Overview Card */}
      <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-mutual-purple/20 flex items-center justify-center">
              <Key className="h-5 w-5 text-mutual-purple" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-content-primary">Platform License</h2>
              {licenseDetail?.licenseName && (
                <p className="text-sm text-content-tertiary">{licenseDetail.licenseName}</p>
              )}
            </div>
          </div>
          <StatusBadge status={licenseStatus.status} />
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Seats */}
          <div className="bg-surface-secondary rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-content-tertiary" />
              <span className="text-sm text-content-tertiary">Seats</span>
            </div>
            <div className="text-2xl font-bold text-content-primary">
              {licenseStatus.seatsUsed}
              <span className="text-base font-normal text-content-tertiary">
                {licenseStatus.seatLimit != null ? ` / ${licenseStatus.seatLimit}` : " (unlimited)"}
              </span>
            </div>
            {licenseStatus.seatsAvailable != null && (
              <p className="text-xs text-content-tertiary mt-1">
                {licenseStatus.seatsAvailable} seat{licenseStatus.seatsAvailable !== 1 ? "s" : ""} available
              </p>
            )}
          </div>

          {/* Expiry */}
          <div className="bg-surface-secondary rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-content-tertiary" />
              <span className="text-sm text-content-tertiary">Expires</span>
            </div>
            <div className="text-2xl font-bold text-content-primary">
              {licenseStatus.expiresAt ? new Date(licenseStatus.expiresAt).toLocaleDateString() : "Never"}
            </div>
            {licenseStatus.expiresAt && (
              <p className="text-xs text-content-tertiary mt-1">
                {new Date(licenseStatus.expiresAt) > new Date() ? "Active" : "Expired"}
              </p>
            )}
          </div>

          {/* Status */}
          <div className="bg-surface-secondary rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="h-4 w-4 text-content-tertiary" />
              <span className="text-sm text-content-tertiary">Platform</span>
            </div>
            <div className="text-2xl font-bold text-content-primary">
              {licenseStatus.isActive ? "Online" : "Suspended"}
            </div>
          </div>
        </div>
      </div>

      {/* MH Admin: Suspension warning */}
      {isMhStaff && licenseDetail && !licenseDetail.isActive && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-yellow-200">Platform is suspended</p>
            {licenseDetail.suspensionReason && (
              <p className="text-sm text-yellow-300/80 mt-1">Reason: {licenseDetail.suspensionReason}</p>
            )}
            <button
              type="button"
              onClick={handleResume}
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors"
            >
              <Play className="h-3 w-3" />
              Resume Platform
            </button>
          </div>
        </div>
      )}

      {/* MH Admin: Management section */}
      {isMhStaff && licenseDetail && (
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-content-primary">License Configuration</h3>
            {!editing && (
              <button
                type="button"
                onClick={startEditing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-surface-secondary text-content-secondary hover:text-content-primary hover:bg-surface-tertiary transition-colors"
              >
                <Edit3 className="h-3 w-3" />
                Edit
              </button>
            )}
          </div>

          {editing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">Seat Limit</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Unlimited"
                    value={editSeatLimit}
                    onChange={(e) => setEditSeatLimit(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border-primary text-content-primary placeholder:text-content-tertiary focus:outline-none focus:ring-2 focus:ring-mutual-purple/50"
                  />
                  <p className="text-xs text-content-tertiary mt-1">Leave blank for unlimited seats</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">Expires At</label>
                  <input
                    type="datetime-local"
                    value={editExpiresAt}
                    onChange={(e) => setEditExpiresAt(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border-primary text-content-primary focus:outline-none focus:ring-2 focus:ring-mutual-purple/50"
                  />
                  <p className="text-xs text-content-tertiary mt-1">Leave blank for no expiration</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">License Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Acme Corp - Annual"
                    value={editLicenseName}
                    onChange={(e) => setEditLicenseName(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border-primary text-content-primary placeholder:text-content-tertiary focus:outline-none focus:ring-2 focus:ring-mutual-purple/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">Exempt Domains</label>
                  <input
                    type="text"
                    placeholder="mutuallyhuman.com"
                    value={editExemptDomains}
                    onChange={(e) => setEditExemptDomains(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border-primary text-content-primary placeholder:text-content-tertiary focus:outline-none focus:ring-2 focus:ring-mutual-purple/50"
                  />
                  <p className="text-xs text-content-tertiary mt-1">Comma-separated domains exempt from seat count</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-content-secondary mb-1">Internal Notes</label>
                <textarea
                  rows={3}
                  placeholder="Internal notes about this license..."
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border-primary text-content-primary placeholder:text-content-tertiary focus:outline-none focus:ring-2 focus:ring-mutual-purple/50 resize-none"
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-mutual-purple text-white hover:bg-mutual-purple/90 disabled:opacity-50 transition-colors"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-surface-secondary text-content-secondary hover:text-content-primary transition-colors"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow label="Seat Limit" value={licenseDetail.seatLimit?.toString() ?? "Unlimited"} />
              <InfoRow
                label="Expires At"
                value={licenseDetail.expiresAt ? new Date(licenseDetail.expiresAt).toLocaleString() : "Never"}
              />
              <InfoRow label="License Name" value={licenseDetail.licenseName ?? "—"} />
              <InfoRow label="Exempt Domains" value={licenseDetail.exemptDomains?.join(", ") ?? "—"} />
              {licenseDetail.notes && <InfoRow label="Notes" value={licenseDetail.notes} fullWidth />}
            </div>
          )}
        </div>
      )}

      {/* MH Admin: Kill Switch */}
      {isMhStaff && licenseDetail?.isActive && (
        <div className="bg-surface-primary border border-red-500/20 rounded-xl p-6">
          <h3 className="text-base font-semibold text-content-primary mb-2">Kill Switch</h3>
          <p className="text-sm text-content-tertiary mb-4">
            Suspending the platform will immediately lock out all users except Mutually Human staff. This is a
            reversible action.
          </p>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-content-secondary mb-1">
                Reason (optional, shown to users)
              </label>
              <input
                type="text"
                placeholder="e.g. License expired, pending renewal"
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border-primary text-content-primary placeholder:text-content-tertiary focus:outline-none focus:ring-2 focus:ring-red-500/50"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowSuspendConfirm(true)}
              disabled={suspending}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 disabled:opacity-50 transition-colors shrink-0"
            >
              {suspending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pause className="h-4 w-4" />}
              Suspend Platform
            </button>
          </div>
        </div>
      )}

      {/* Suspend Confirmation Dialog */}
      {showSuspendConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-surface-primary border border-border-primary rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-content-primary">Suspend Platform</h3>
            </div>
            <p className="text-sm text-content-secondary mb-2">
              Are you sure you want to suspend the entire platform? This will:
            </p>
            <ul className="text-sm text-content-secondary mb-4 list-disc list-inside space-y-1">
              <li>Immediately lock out all non-exempt users</li>
              <li>Display a suspension notice to anyone who tries to log in</li>
              <li>Remain in effect until manually resumed</li>
            </ul>
            {suspendReason && (
              <div className="mb-4 p-3 bg-surface-secondary rounded-lg">
                <p className="text-xs text-content-tertiary mb-1">Reason shown to users:</p>
                <p className="text-sm text-content-primary">{suspendReason}</p>
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowSuspendConfirm(false)}
                className="px-4 py-2 text-sm text-content-secondary hover:text-content-primary transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSuspendConfirm(false);
                  handleSuspend();
                }}
                disabled={suspending}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 disabled:opacity-50 transition-colors"
              >
                {suspending ? "Suspending..." : "Yes, Suspend Platform"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------

function InfoRow({ label, value, fullWidth }: { label: string; value: string; fullWidth?: boolean }) {
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <dt className="text-sm text-content-tertiary">{label}</dt>
      <dd className="text-sm text-content-primary mt-0.5">{value}</dd>
    </div>
  );
}
